import React, { useState, useEffect, useCallback } from 'react';
import { supabase, insertRow, updateRow } from '../db/engine';
import { reaplicarSacoPoliza } from '../db/saco';
import { generarComisionNegativa } from '../db/comisiones';
import { Calculator, FileText } from 'lucide-react';
import { fmtFecha } from '../utils/fecha';

import { fmtMoneda as fmt$ } from '../utils/moneda';
const r2 = x => Math.round((Number(x) || 0) * 100) / 100;

const tipoLabel = {
  cobro: 'Cobro de exceso',
  devolucion: 'Devolución (nota de crédito)',
  sin_ajuste: 'Sin ajuste',
};

export default function RegularizacionPanel({ poliza, onChange }) {
  const [cuotas, setCuotas] = useState([]);
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [detalleId, setDetalleId] = useState(null);

  const esDeclarativa = !!(poliza && poliza.es_declarativa);
  const tieneDeposito = (parseFloat(poliza && poliza.prima_deposito) || 0) > 0;

  const cargar = useCallback(async () => {
    if (!poliza || !poliza.id) return;
    setLoading(true);
    try {
      const { data: pagos } = await supabase
        .from('pagos')
        .select('id,monto,monto_pagado,estado,fecha_vencimiento,cuota_num')
        .eq('poliza_id', poliza.id)
        .not('cuota_num', 'is', null);
      const { data: regsData } = await supabase
        .from('poliza_regularizaciones')
        .select('*')
        .eq('poliza_id', poliza.id)
        .order('creado_en', { ascending: false });
      setCuotas(pagos || []);
      setRegs(regsData || []);
    } catch (e) {
      console.error('Error cargando regularización:', e);
      alert('No se pudieron cargar los datos de regularización: ' + (e.message || e));
    } finally {
      setLoading(false);
    }
  }, [poliza]);

  useEffect(() => { cargar(); }, [cargar]);

  // ── Cálculos ──
  const impuestoPct = parseFloat(poliza && poliza.impuesto) || 0;
  const depositoSinImp = parseFloat(poliza && poliza.prima_deposito) || 0;
  const depositoConImp = r2(depositoSinImp * (1 + impuestoPct / 100));
  const depositoEsGanado = !!(poliza && poliza.deposito_es_ganado);

  const primaGanadaTotal = r2((cuotas || []).reduce((acc, c) => acc + (parseFloat(c.monto) || 0), 0));
  const diferencia = r2(primaGanadaTotal - depositoConImp);

  let tipoAjuste = 'sin_ajuste';
  if (diferencia > 0.01) tipoAjuste = 'cobro';
  else if (diferencia < -0.01 && !depositoEsGanado) tipoAjuste = 'devolucion';

  const yaGenerada = (regs || []).length > 0;

  const fechaCorte = (poliza && poliza.fecha_vencimiento) || new Date().toISOString().slice(0, 10);

  // ── Generar regularización ──
  const generar = async () => {
    if (working || yaGenerada) return;
    const msg =
      tipoAjuste === 'cobro'
        ? `Se generará la regularización de cierre y se creará un SALDO POR COBRAR de ${fmt$(diferencia)} al cliente (exceso declarado sobre el depósito).\n\n¿Continuar?`
        : tipoAjuste === 'devolucion'
          ? `Se generará la regularización de cierre y se creará una NOTA DE CRÉDITO (saldo negativo) de ${fmt$(diferencia)} a favor del cliente.\n\n¿Continuar?`
          : `Se generará la regularización de cierre SIN ajuste (la prima ganada coincide con el depósito).\n\n¿Continuar?`;
    if (!window.confirm(msg)) return;

    setWorking(true);
    try {
      const reg = await insertRow('poliza_regularizaciones', {
        poliza_id: poliza.id,
        fecha_corte: fechaCorte,
        prima_deposito: depositoConImp,
        prima_ganada_total: primaGanadaTotal,
        diferencia,
        tipo_ajuste: tipoAjuste,
        estado: 'pendiente',
        notas: null,
      });

      if (tipoAjuste === 'cobro' || tipoAjuste === 'devolucion') {
        const saldo = await insertRow('pagos', {
          poliza_id: poliza.id,
          monto: diferencia, // negativo si es devolución
          estado: 'pendiente',
          fecha_vencimiento: fechaCorte,
          metodo: (poliza && poliza.forma_pago) || null,
          cuota_num: null,
          origen: 'ajuste',
        });
        await updateRow('poliza_regularizaciones', reg.id, { pago_id: saldo.id });
        // Devolución (prima ganada < depósito): la aseguradora descuenta al corredor la
        // comisión cobrada de más → comisión negativa (clawback) anclada al saldo negativo.
        if (tipoAjuste === 'devolucion' && saldo?.id) {
          await generarComisionNegativa(poliza.id, saldo.id, diferencia);
        }
      }

      await reaplicarSacoPoliza(poliza.id);
      // Cerrar el recordatorio de regularización de esta póliza en el CRM (ya quedó hecha).
      try {
        await supabase.from('crm_gestiones')
          .update({ estado: 'cerrado', actualizado_en: new Date().toISOString() })
          .eq('poliza_id', poliza.id).eq('tipo', 'seguimiento')
          .like('titulo', 'Regularización de cierre%').in('estado', ['abierto', 'en_proceso']);
      } catch { /* no crítico */ }
      await cargar();
      if (onChange) onChange();
    } catch (e) {
      console.error('Error generando regularización:', e);
      alert('No se pudo generar la regularización: ' + (e.message || e));
    } finally {
      setWorking(false);
    }
  };

  // ── Render: casos informativos ──
  if (!esDeclarativa) {
    return (
      <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--silver)', fontSize: 13 }}>
        Esta póliza no es declarativa. La regularización de cierre solo aplica a pólizas declarativas con prima depósito.
      </div>
    );
  }
  if (!tieneDeposito) {
    return (
      <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--silver)', fontSize: 13 }}>
        Esta póliza declarativa no tiene prima depósito registrada. No es posible calcular la regularización de cierre.
      </div>
    );
  }
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--silver)', fontSize: 13 }}>
        Cargando regularización…
      </div>
    );
  }

  const card = {
    flex: '1 1 160px',
    padding: '12px 14px',
    background: 'rgba(var(--steel-rgb),0.3)',
    borderRadius: 10,
    border: '1px solid rgba(var(--silver-rgb),0.15)',
  };
  const cardLabel = { fontSize: 11, color: 'var(--silver)', textTransform: 'uppercase', marginBottom: 6 };

  const difColor = tipoAjuste === 'cobro' ? '#22c55e' : tipoAjuste === 'devolucion' ? '#ef4444' : 'var(--silver)';
  const difBadge = tipoAjuste === 'cobro' ? 'badge-success' : tipoAjuste === 'devolucion' ? 'badge-danger' : 'badge-warning';

  return (
    <div>
      {/* ── Resumen ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Calculator size={16} color="var(--sky-light)" />
        <div style={{ fontSize: 13, fontWeight: 700 }}>Regularización de cierre</div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div style={card}>
          <div style={cardLabel}>Prima depósito</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{fmt$(depositoConImp)}</div>
          <div style={{ fontSize: 11, color: 'var(--silver)', marginTop: 2 }}>
            Sin imp.: {fmt$(depositoSinImp)} · Imp. {impuestoPct}%
          </div>
          {depositoEsGanado && (
            <div style={{ fontSize: 11, color: 'var(--text-warning)', marginTop: 4 }}>Depósito ganado (no reembolsable)</div>
          )}
        </div>

        <div style={card}>
          <div style={cardLabel}>Total declarado (prima ganada)</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-success)' }}>{fmt$(primaGanadaTotal)}</div>
          <div style={{ fontSize: 11, color: 'var(--silver)', marginTop: 2 }}>{cuotas.length} declaración(es)</div>
        </div>

        <div style={card}>
          <div style={cardLabel}>Diferencia</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: difColor }}>
            {diferencia > 0 ? '+' : ''}{fmt$(diferencia)}
          </div>
          <div style={{ marginTop: 6 }}>
            <span className={`badge ${difBadge}`}>{tipoLabel[tipoAjuste]}</span>
          </div>
        </div>
      </div>

      {/* ── Botón generar ── */}
      {!yaGenerada && (
        <div style={{ marginBottom: 16 }}>
          <button className="btn btn-primary btn-sm" onClick={generar} disabled={working}>
            <Calculator size={13} /> {working ? 'Generando…' : 'Generar regularización de cierre'}
          </button>
          <div style={{ fontSize: 11, color: 'var(--silver)', marginTop: 6 }}>
            {tipoAjuste === 'cobro' && 'Creará un saldo por cobrar al cliente por el exceso declarado.'}
            {tipoAjuste === 'devolucion' && 'Creará una nota de crédito (saldo negativo) a favor del cliente.'}
            {tipoAjuste === 'sin_ajuste' && 'Registrará el cierre sin generar saldo (depósito y prima ganada coinciden).'}
          </div>
        </div>
      )}

      {/* ── Regularizaciones generadas ── */}
      <div>
        <div style={{ fontSize: 11, color: 'var(--silver)', textTransform: 'uppercase', marginBottom: 8 }}>
          Regularizaciones generadas
        </div>
        {regs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--silver)', fontSize: 13 }}>
            Aún no se ha generado ninguna regularización de cierre.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {regs.map((rg) => {
              const dif = parseFloat(rg.diferencia) || 0;
              const dColor = rg.tipo_ajuste === 'cobro' ? '#22c55e' : rg.tipo_ajuste === 'devolucion' ? '#ef4444' : 'var(--silver)';
              const dBadge = rg.tipo_ajuste === 'cobro' ? 'badge-success' : rg.tipo_ajuste === 'devolucion' ? 'badge-danger' : 'badge-warning';
              const abierto = detalleId === rg.id;
              return (
                <div
                  key={rg.id}
                  style={{ padding: '12px 14px', background: 'rgba(var(--steel-rgb),0.3)', borderRadius: 10, border: '1px solid rgba(var(--silver-rgb),0.15)' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <FileText size={13} color="var(--sky-light)" /> Cierre al {fmtFecha(rg.fecha_corte) || '—'}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--silver)', marginTop: 4 }}>
                        Registrada: {rg.creado_en ? fmtFecha(rg.creado_en) : '—'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: dColor }}>
                        {dif > 0 ? '+' : ''}{fmt$(dif)}
                      </div>
                      <span className={`badge ${dBadge}`} style={{ marginTop: 4 }}>{tipoLabel[rg.tipo_ajuste] || rg.tipo_ajuste}</span>
                      <div style={{ marginTop: 4 }}>
                        <span className="badge badge-warning">{rg.estado || '—'}</span>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: 8 }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setDetalleId(abierto ? null : rg.id)}
                    >
                      {abierto ? 'Ocultar detalle' : 'Ver detalle'}
                    </button>
                  </div>

                  {abierto && (
                    <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8 }}>
                      <div style={{ fontSize: 12, color: 'var(--silver)' }}>
                        Prima ganada total: <span style={{ color: 'var(--text-success)' }}>{fmt$(rg.prima_ganada_total)}</span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--silver)' }}>
                        Prima depósito: <span style={{ color: 'var(--text)' }}>{fmt$(rg.prima_deposito)}</span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--silver)' }}>
                        Diferencia: <span style={{ color: dColor }}>{fmt$(rg.diferencia)}</span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--silver)' }}>
                        Tipo de ajuste: <span style={{ color: 'var(--text)' }}>{tipoLabel[rg.tipo_ajuste] || rg.tipo_ajuste}</span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--silver)' }}>
                        Saldo generado (pago_id): <span style={{ color: 'var(--sky-light)' }}>{rg.pago_id || '—'}</span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--silver)' }}>
                        Endoso (endoso_id): <span style={{ color: 'var(--sky-light)' }}>{rg.endoso_id || '—'}</span>
                      </div>
                      {rg.notas && (
                        <div style={{ gridColumn: '1 / -1', fontSize: 12, color: 'var(--silver)' }}>
                          Notas: <span style={{ color: 'var(--text)' }}>{rg.notas}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
